import { HideImage, QuestionMark } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid, TextField } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInManufacturersLabelPhotoDto, vehicleCheckInManufacturersLabelPhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface ManufacturersLabelPhotoDialogProps {
  initialValues: VehicleCheckInManufacturersLabelPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInManufacturersLabelPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function ManufacturersLabelPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: ManufacturersLabelPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInManufacturersLabelPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ errors, handleBlur, handleChange, isSubmitting, setFieldValue, submitForm, touched, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title="Manufacturer's Label Photo Details" />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt="Manufacturer's Label"
                      file={values.vehicleCheckInManufacturersLabelPhotoFile}
                      placeholderSrc='/1 Manufacturers Label.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='mfr-label-browse'
                    cameraInputId='mfr-label-camera'
                    disabled={isSubmitting || isLoading || values.vehicleCheckInIsManufacturersLabelPhotoMissing}
                    onFileChosen={file => setFieldValue('vehicleCheckInManufacturersLabelPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        values.vehicleCheckInIsManufacturersLabelPhotoMissing ||
                        !Boolean(values.documentGuid)
                      }
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm("Are you sure you would like to delete the uploaded Manufacturer's Label?")) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={<QuestionMark />}
                      fullWidth
                      onClick={() => {
                        setFieldValue(
                          'vehicleCheckInIsManufacturersLabelPhotoMissing',
                          !values.vehicleCheckInIsManufacturersLabelPhotoMissing
                        )
                      }}
                      variant={values.vehicleCheckInIsManufacturersLabelPhotoMissing ? 'contained' : 'outlined'}
                    >
                      MANUFACTURER'S LABEL MISSING
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      error={Boolean(
                        touched.vehicleCheckInManufacturersLabelMissingNote &&
                          errors.vehicleCheckInManufacturersLabelMissingNote
                      )}
                      disabled={isSubmitting || isLoading || !values.vehicleCheckInIsManufacturersLabelPhotoMissing}
                      fullWidth
                      helperText={
                        touched.vehicleCheckInManufacturersLabelMissingNote &&
                        errors.vehicleCheckInManufacturersLabelMissingNote
                      }
                      label='Manufacturer Label Missing Note'
                      name='vehicleCheckInManufacturersLabelMissingNote'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.vehicleCheckInManufacturersLabelMissingNote || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || If the Manufacturer Label Missing image is pressed, the “Manufacturer Label Missing Note” field becomes
                      required. || Pressing “Submit” will verify that a photo has been uploaded or, if a user pressed
                      “Manufacturer Label Missing”, that the “Manufacturer Label Missing Note” field is not blank. || Delete
                      Uploaded Photo can only be pressed if a user has uploaded an image. || Pressing “Submit” will associate
                      the user-uploaded image with the vehicle record or add a record to the Manufacturer Label Missing to do
                      list. || Pressing “Previous” will go to the previous photo without saving changes. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
